import React from 'react';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { Box, Button, Chip, Stack, TextField } from '@mui/material';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import SortableRoomImageSlot from './SortableSlot';
import type { RoomImage } from './utils';

interface Props {
  canToggleHidden?: boolean;
  hiddenImagePublicIds: Array<string>;
  image?: RoomImage;
  isMetadataEditable?: boolean;
  onDeleteImage: (publicId: string) => void;
  onEditImage: (publicId: string) => void;
  onOpenPicker: () => void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onUpdateImageTitle: (publicId: string, title: string) => void;
  onUploadImages?: (files: Array<File>) => void;
  slotNumber: number;
}

export default function ImagesByRoomSlot({
  canToggleHidden,
  hiddenImagePublicIds,
  image,
  isMetadataEditable = false,
  onDeleteImage,
  onEditImage,
  onOpenPicker,
  onToggleHiddenImage,
  onUpdateImageTitle,
  onUploadImages,
  slotNumber,
}: Props) {
  const slotLabel = slotNumber === 1 ? `#${slotNumber} Hero` : `#${slotNumber}`;
  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    onUploadImages?.(files);
  };

  if (image) {
    return (
      <Stack
        spacing={isMetadataEditable ? 1 : 0}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          p: isMetadataEditable ? 1 : 0,
          border: isMetadataEditable ? 1 : 0,
          borderColor: 'divider',
          borderRadius: isMetadataEditable ? 1 : 0,
          bgcolor: isMetadataEditable ? 'background.paper' : 'transparent',
        }}
      >
        <SortableRoomImageSlot
          canToggleHidden={!!canToggleHidden}
          hasFrame={!isMetadataEditable}
          image={image}
          isHidden={hiddenImagePublicIds.includes(image.publicId)}
          onDeleteImage={onDeleteImage}
          onEditImage={onEditImage}
          onToggleHiddenImage={onToggleHiddenImage}
          slotLabel={slotLabel}
        />
        {isMetadataEditable && (
          <TextField
            fullWidth
            size="small"
            label="Short description"
            value={image.title ?? ''}
            onChange={(event) => onUpdateImageTitle(image.publicId, event.target.value)}
          />
        )}
      </Stack>
    );
  }

  if (isMetadataEditable) {
    return (
      <Button
        component={onUploadImages ? 'label' : 'button'}
        variant="text"
        onClick={onUploadImages ? undefined : onOpenPicker}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          overflow: 'hidden',
          width: '100%',
          minWidth: 0,
          p: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'grey.50',
          textTransform: 'none',
          '&:hover': {
            bgcolor: 'grey.100',
          },
        }}
      >
        <Box sx={{ width: '100%', aspectRatio: '16 / 9' }} />
        <Box sx={{ height: METADATA_DESCRIPTION_FIELD_HEIGHT }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.400',
          }}
        >
          <AddPhotoAlternateOutlinedIcon fontSize="large" />
        </Box>
        <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
        {onUploadImages && (
          <input hidden accept={ALLOWED_IMAGE_UPLOAD_ACCEPT} multiple type="file" onChange={handleUploadInputChange} />
        )}
      </Button>
    );
  }

  return (
    <Button
      component={onUploadImages ? 'label' : 'button'}
      variant="text"
      onClick={onUploadImages ? undefined : onOpenPicker}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '16 / 9',
        alignSelf: 'stretch',
        width: '100%',
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1,
        bgcolor: 'grey.50',
        minWidth: 0,
        p: 0,
        '&:hover': {
          bgcolor: 'grey.100',
        },
      }}
    >
      <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', color: 'grey.400' }}>
        <AddPhotoAlternateOutlinedIcon fontSize="large" />
      </Stack>
      <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
      {onUploadImages && (
        <input hidden accept={ALLOWED_IMAGE_UPLOAD_ACCEPT} multiple type="file" onChange={handleUploadInputChange} />
      )}
    </Button>
  );
}

const METADATA_DESCRIPTION_FIELD_HEIGHT = 40;

const roomImageSlotChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};
